import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { python } from "@codemirror/lang-python";
import { type Language, StreamLanguage } from "@codemirror/language";
import { css } from "@codemirror/legacy-modes/mode/css";
import { dockerFile } from "@codemirror/legacy-modes/mode/dockerfile";
import { go } from "@codemirror/legacy-modes/mode/go";
import { ruby } from "@codemirror/legacy-modes/mode/ruby";
import { rust } from "@codemirror/legacy-modes/mode/rust";
import { shell } from "@codemirror/legacy-modes/mode/shell";
import { standardSQL } from "@codemirror/legacy-modes/mode/sql";
import { xml } from "@codemirror/legacy-modes/mode/xml";
import { yaml } from "@codemirror/legacy-modes/mode/yaml";
import { highlightTree, tagHighlighter } from "@lezer/highlight";
import { Fragment, type ReactNode } from "react";
import { syntaxHighlightStyles } from "./syntaxHighlightStyles";

const highlighter = tagHighlighter(
  syntaxHighlightStyles.map(({ name, tag }) => ({
    tag,
    class: `tok-${name}`,
  }))
);

const languageCache = new Map<string, Language>();

function createLanguage(key: string): Language | null {
  switch (key) {
    case "ts":
    case "typescript":
      return javascript({ typescript: true }).language;
    case "tsx":
      return javascript({ typescript: true, jsx: true }).language;
    case "js":
    case "javascript":
      return javascript().language;
    case "jsx":
      return javascript({ jsx: true }).language;
    case "json":
      return json().language;
    case "py":
    case "python":
      return python().language;
    case "sh":
    case "bash":
    case "shell":
    case "zsh":
      return StreamLanguage.define(shell);
    case "dockerfile":
      return StreamLanguage.define(dockerFile);
    case "sql":
    case "postgres":
    case "postgresql":
    case "mysql":
    case "sqlite":
      return StreamLanguage.define(standardSQL);
    case "html":
    case "xml":
    case "svg":
      return StreamLanguage.define(xml);
    case "css":
    case "scss":
    case "less":
      return StreamLanguage.define(css);
    case "yaml":
    case "yml":
      return StreamLanguage.define(yaml);
    case "rust":
    case "rs":
      return StreamLanguage.define(rust);
    case "go":
      return StreamLanguage.define(go);
    case "ruby":
    case "rb":
      return StreamLanguage.define(ruby);
    default:
      return null;
  }
}

function getLanguage(lang: string): Language | null {
  const key = lang.toLowerCase();
  const cached = languageCache.get(key);
  if (cached) return cached;
  const language = createLanguage(key);
  if (language) languageCache.set(key, language);
  return language;
}

export function highlightCode(code: string, lang: string): ReactNode {
  const language = getLanguage(lang);
  if (!language) return code;
  const tree = language.parser.parse(code);
  const nodes: ReactNode[] = [];
  let key = 0;
  let pos = 0;
  highlightTree(tree, highlighter, (from, to, classes) => {
    if (from > pos) {
      nodes.push(<Fragment key={key++}>{code.slice(pos, from)}</Fragment>);
    }
    nodes.push(
      <span className={classes} key={key++}>
        {code.slice(from, to)}
      </span>
    );
    pos = to;
  });
  if (pos < code.length) {
    nodes.push(<Fragment key={key}>{code.slice(pos)}</Fragment>);
  }
  return nodes;
}
